import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DateInput from "@/components/DateInput";
import api, { fmtMoney, fmtDate, fmtNum, todayISO, calcVencimiento } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Trash, UploadSimple, Warning, PencilSimple, CaretDown, CaretRight,
  FunnelSimple, X, Receipt, FileXls, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import BatchDetailDialog from "@/components/BatchDetailDialog";
import ProductoCombobox from "@/components/ProductoCombobox";
import ProveedorCombobox from "@/components/ProveedorCombobox";
import MoneyInput from "@/components/MoneyInput";
import NumberInput from "@/components/ui/NumberInput";

const TIPOS_COMPROBANTE = [
  "FACTURA A", "FACTURA B", "FACTURA C", "FACTURA M",
  "NOTA DE CREDITO A", "NOTA DE CREDITO B",
  "NOTA DE DEBITO A", "NOTA DE DEBITO B",
  "REMITO", "TICKET", "OTRO",
];

const IVA_OPCIONES = [
  { value: 21, label: "21%" },
  { value: 10.5, label: "10,5%" },
  { value: 27, label: "27%" },
  { value: 0, label: "Exento" },
];

export const TIPOS_TRIBUTO = [
  { value: "retencion_iva", label: "Retención IVA" },
  { value: "retencion_ganancias", label: "Retención Ganancias" },
  { value: "retencion_iibb", label: "Retención IIBB" },
  { value: "retencion_suss", label: "Retención SUSS" },
  { value: "percepcion_iva", label: "Percepción IVA" },
  { value: "percepcion_iibb", label: "Percepción IIBB" },
];

const tributoLabel = (t) => TIPOS_TRIBUTO.find((x) => x.value === t)?.label || t;

const EMPTY_FILTERS = {
  tipo_comprobante: "__all__",
  contraparte_id: "__all__",
  estado: "__all__",
  fecha_desde: "",
  fecha_hasta: "",
  q: "",
  solo_con_diferencias: false,
};

const initForm = () => ({
  numero: "",
  tipo_comprobante: "FACTURA A",
  fecha: todayISO(),
  fecha_vencimiento: todayISO(),
  contraparte_id: "",
  moneda: "ARS",
  cotizacion: 1,
  iva_pct: 10.5,
  romaneo: "",
  observaciones: "",
  items: [{ descripcion: "", codigo: "", cantidad: 1, precio_unitario: 0, iva_pct: 10.5 }],
  percepciones: [],
  medio_pago: "cta_cte",
  actualiza_costo: true,      // (compras) actualizar el "último costo" del insumo/producto linkeado. La compra NO mueve stock.
  vto_touched: false,
});

const estadoBadge = {
  pendiente: "text-amber-700 bg-amber-50",
  parcial: "text-blue-700 bg-blue-50",
  pagada: "text-emerald-700 bg-emerald-50",
};

export default function ComprasPage() {
  const [compras, setCompras] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [tiposComprobante, setTiposComprobante] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initForm());
  const [importing, setImporting] = useState(false);
  const [dupPrompt, setDupPrompt] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [delPagos, setDelPagos] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [batches, setBatches] = useState([]);
  const [tab, setTab] = useState("comprobantes");
  const [delBatch, setDelBatch] = useState(null);
  const [viewBatch, setViewBatch] = useState(null);
  const [pagosAsociados, setPagosAsociados] = useState([]);
  const [newProvOpen, setNewProvOpen] = useState(false);
  const [newProvForm, setNewProvForm] = useState({ nombre: "", cuit: "", condicion_iva: "Responsable Inscripto" });
  const [savingProv, setSavingProv] = useState(false);
  const [pendingProvId, setPendingProvId] = useState(null);
  const [prefillIngresoId, setPrefillIngresoId] = useState(null);
  const fileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const buildParams = () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "solo_con_diferencias") return;   // filtro client-side
      if (v && v !== "__all__" && v !== "") p[k] = v;
    });
    return p;
  };

  const load = async () => {
    const { data } = await api.get("/compras", { params: buildParams() });
    setCompras(data);
    const { data: cps } = await api.get("/proveedores");
    setProveedores(cps);
    const { data: tps } = await api.get("/compras/tipos-comprobante");
    setTiposComprobante(Array.from(new Set([...TIPOS_COMPROBANTE, ...tps])).sort());
    const { data: cjs } = await api.get("/cajas");
    setCajas(cjs.filter((c) => c.moneda === "ARS"));
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters]);

  const loadBatches = async () => {
    const { data } = await api.get("/import-batches");
    setBatches(data.filter((b) => b.type === "compras"));
  };

  const revertBatch = async () => {
    if (!delBatch) return;
    try {
      const { data } = await api.delete(`/import-batches/${delBatch.id}`);
      toast.success(`Lote revertido: ${data.deleted} compras eliminadas`);
      setDelBatch(null);
      loadBatches();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al revertir");
    }
  };

  useEffect(() => {
    if (tab === "historial") loadBatches();
  }, [tab]);

  // Pre-carga desde "Ingreso de Mercadería" — cuando llega `?ingreso_id=...`
  useEffect(() => {
    const ingId = searchParams.get("ingreso_id");
    if (!ingId || prefillIngresoId === ingId) return;
    (async () => {
      try {
        const { data: ing } = await api.get(`/ingresos-mercaderia/${ingId}`);
        if (ing.estado === "con_compra") {
          toast.info("Este ingreso ya tiene una compra asociada");
          searchParams.delete("ingreso_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        // Pre-cargar el último costo de cada producto vinculado del catálogo
        // para que "precio_unitario" no arranque en 0. `productos.ultimo_costo`
        // se guarda CON IVA incluido → dividimos por (1 + iva/100) para el neto.
        // También pasamos los `codigo` como fallback por si algún ítem no tenía
        // producto_id vinculado (ingreso viejo) pero sí tiene código.
        const prodIds = Array.from(
          new Set((ing.items || []).map((it) => it.producto_id).filter(Boolean))
        );
        let costMap = {};
        let costByCodigo = {};
        if (prodIds.length) {
          try {
            const { data } = await api.post("/productos/bulk-info", { ids: prodIds });
            costMap = data || {};
            // También indexamos por codigo para futuras búsquedas por SKU
            Object.entries(costMap).forEach(([pid, info]) => {
              if (info?.codigo) costByCodigo[String(info.codigo).trim()] = { ...info, id: pid };
            });
            console.log("[Compras/Ingreso] bulk-info", prodIds.length, "productos →", costMap);
          } catch (err) {
            console.warn("[Compras/Ingreso] bulk-info falló:", err);
          }
        }
        setPrefillIngresoId(ingId);
        setEditingId(null);
        setForm({
          ...initForm(),
          fecha: ing.fecha || todayISO(),
          fecha_vencimiento: ing.fecha || todayISO(),
          contraparte_id: ing.proveedor_id || "",
          observaciones: `Compra desde Ingreso · ${ing.fecha}${ing.observaciones ? ` · ${ing.observaciones}` : ""}`,
          items: (ing.items || []).map((it) => {
            // 1) match por producto_id, 2) match por código si tiene, 3) sin match
            let info = it.producto_id ? costMap[it.producto_id] : null;
            let matchedProdId = it.producto_id || null;
            if (!info && it.codigo && costByCodigo[String(it.codigo).trim()]) {
              info = costByCodigo[String(it.codigo).trim()];
              matchedProdId = info.id;
            }
            const ivaPct = info?.iva_pct != null ? info.iva_pct : 10.5;
            const ultCostoConIva = info?.ultimo_costo || 0;
            const precioNeto = ultCostoConIva > 0
              ? +(ultCostoConIva / (1 + (ivaPct || 0) / 100)).toFixed(4)
              : 0;
            return {
              descripcion: it.descripcion,
              codigo: it.codigo || "",
              kilos: parseFloat(it.peso_real) || 0,
              cantidad: parseFloat(it.peso_real) || 0,
              precio_unitario: precioNeto,
              iva_pct: ivaPct,
              producto_id: matchedProdId || undefined,
              unidad: info?.unidad || undefined,
              _prefill_from_cat: precioNeto > 0,  // marcador visual
            };
          }),
          vto_touched: false,
        });
        setOpen(true);
        const conCosto = (ing.items || []).filter((it) => it.producto_id && (costMap[it.producto_id]?.ultimo_costo || 0) > 0).length;
        toast.success(
          conCosto > 0
            ? `Ingreso pre-cargado · ${conCosto} ítem(s) con último costo del catálogo`
            : "Ingreso pre-cargado. Completá número, tipo y precios."
        );
      } catch {
        toast.error("No se pudo cargar el Ingreso de Mercadería");
        searchParams.delete("ingreso_id");
        setSearchParams(searchParams, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    setForm({ ...form, items });
  };

  const addItem = () =>
    setForm({ ...form, items: [...form.items, { descripcion: "", codigo: "", cantidad: 1, precio_unitario: 0, iva_pct: form.iva_pct }] });

  const removeItem = (i) =>
    setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const addPercepcion = () =>
    setForm({ ...form, percepciones: [...form.percepciones, { tipo: "percepcion_iva", monto: 0 }] });

  const setPercepcion = (i, field, value) => {
    const percepciones = [...form.percepciones];
    percepciones[i] = { ...percepciones[i], [field]: value };
    setForm({ ...form, percepciones });
  };

  const removePercepcion = (i) =>
    setForm({ ...form, percepciones: form.percepciones.filter((_, idx) => idx !== i) });

  // Cálculos: IVA por línea (fallback al iva del comprobante)
  const subtotal = form.items.reduce(
    (s, it) => s + (parseFloat(it.cantidad || 0) * parseFloat(it.precio_unitario || 0)), 0);
  const iva = form.items.reduce((s, it) => {
    const neto = (parseFloat(it.cantidad || 0) * parseFloat(it.precio_unitario || 0));
    const rate = it.iva_pct != null ? parseFloat(it.iva_pct) : parseFloat(form.iva_pct || 0);
    return s + (neto * (rate || 0) / 100);
  }, 0);
  const percepcionesTotal = form.percepciones.reduce((s, p) => s + parseFloat(p.monto || 0), 0);
  const total = subtotal + iva + percepcionesTotal;

  const openNew = () => {
    setEditingId(null);
    setForm(initForm());
    setOpen(true);
  };

  const openEdit = async (f) => {
    setEditingId(f.id);
    // Detectar medio de pago actual
    let medioActual = "cta_cte";
    let pagos = [];
    try {
      const { data } = await api.get(`/compras/${f.id}/pagos-asociados`);
      pagos = data || [];
      const exclusivos = pagos.filter(
        (p) => Array.isArray(p.facturas_aplicadas) && p.facturas_aplicadas.length === 1 && p.facturas_aplicadas[0] === f.id
      );
      if (exclusivos.length === 1 && pagos.length === 1) {
        medioActual = exclusivos[0].caja_id;
      }
    } catch (_) {}
    setPagosAsociados(pagos);
    setForm({
      numero: f.numero || "",
      tipo_comprobante: f.tipo_comprobante || "FACTURA A",
      fecha: f.fecha,
      fecha_vencimiento: (f.fecha_vencimiento || f.fecha || "").slice(0, 10),
      contraparte_id: f.contraparte_id,
      moneda: f.moneda || "ARS",
      cotizacion: f.cotizacion || 1,
      iva_pct: f.iva_pct ?? 21,
      romaneo: f.romaneo || "",
      observaciones: f.observaciones || "",
      items: f.items?.length > 0 ? f.items.map((it) => ({
        descripcion: it.descripcion || "",
        codigo: it.codigo || "",
        cantidad: it.cantidad || 1,
        precio_unitario: it.precio_unitario || 0,
        iva_pct: it.iva_pct ?? f.iva_pct ?? 21,
      })) : [{ descripcion: "", codigo: "", cantidad: 1, precio_unitario: 0, iva_pct: f.iva_pct ?? 21 }],
      percepciones: (f.percepciones || []).map((p) => ({ tipo: p.tipo, monto: p.monto, observacion: p.observacion || "" })),
      medio_pago: medioActual,
      medio_pago_original: medioActual,
      vto_touched: true,
      // Al editar, el flag de costo queda tildado por defecto.
      actualiza_costo: true,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.contraparte_id) return toast.error("Elegí un proveedor");
    const cp = proveedores.find((c) => c.id === form.contraparte_id);
    const items = form.items.map((it) => {
      const qty = parseFloat(it.cantidad || 1);
      const subt = qty * parseFloat(it.precio_unitario || 0);
      // Cuando el ítem trae `kilos` (ej. cargado desde un Ingreso de Mercadería)
      // se preservan. Si viene desde un Ingreso, kilos sigue a cantidad para
      // que el operador pueda editar el peso real en la columna CANT.
      let kilos = it.kilos != null && it.kilos !== "" ? parseFloat(it.kilos) : 0;
      if (prefillIngresoId) kilos = qty;
      return {
        descripcion: it.descripcion || "",
        codigo: it.codigo || null,
        kilos: kilos || 0,
        cantidad: qty,
        precio_unitario: parseFloat(it.precio_unitario || 0),
        subtotal: subt,
        iva_pct: it.iva_pct != null ? parseFloat(it.iva_pct) : null,
        insumo_id: it.insumo_id || null,
        producto_id: it.producto_id || null,
        categoria_gasto: it.categoria_gasto || null,
      };
    });
    const payload = {
      numero: form.numero,
      tipo_comprobante: form.tipo_comprobante,
      fecha: form.fecha,
      fecha_vencimiento: form.fecha_vencimiento || form.fecha,
      contraparte_id: form.contraparte_id,
      contraparte_nombre: cp?.nombre || "",
      items,
      iva_pct: parseFloat(form.iva_pct || 0),
      percepciones: form.percepciones
        .filter((p) => parseFloat(p.monto || 0) > 0)
        .map((p) => ({ tipo: p.tipo, monto: parseFloat(p.monto), observacion: p.observacion || null })),
      moneda: form.moneda,
      cotizacion: parseFloat(form.cotizacion || 1),
      romaneo: form.romaneo,
      observaciones: form.observaciones,
      actualiza_costo: !!form.actualiza_costo,
    };
    if (prefillIngresoId) {
      payload.ingreso_id = prefillIngresoId;
    }
    try {
      if (editingId) {
        // Sólo enviar medio_pago si el usuario lo cambió (compara vs. el original al abrir)
        const putPayload = { ...payload };
        if (form.medio_pago && form.medio_pago !== form.medio_pago_original) {
          putPayload.medio_pago = form.medio_pago;
        }
        await api.put(`/compras/${editingId}`, putPayload);
        toast.success("Compra actualizada");
      } else {
        const { data: created } = await api.post("/compras", payload);
        toast.success("Compra registrada");

        // Medio de pago automático
        if (form.medio_pago && form.medio_pago !== "cta_cte" && created?.total > 0
            && !(created?.tipo_comprobante || "").toUpperCase().startsWith("NOTA DE CR")) {
          try {
            await api.post("/cobros-pagos", {
              tipo: "pago",
              fecha: form.fecha,
              contraparte_id: form.contraparte_id,
              caja_id: form.medio_pago,
              monto: created.total,
              moneda: form.moneda,
              facturas_aplicadas: [created.id],
              concepto: `Pago automático · ${created.tipo_comprobante || ""} ${created.numero}`.trim(),
              retenciones: [],
            });
            toast.success("Pago registrado en la caja");
          } catch (e) {
            toast.error("Compra creada, pero el pago falló. Registralo manualmente.");
          }
        }
      }
      setOpen(false);
      setEditingId(null);
      setForm(initForm());
      if (prefillIngresoId) {
        setPrefillIngresoId(null);
        searchParams.delete("ingreso_id");
        setSearchParams(searchParams, { replace: true });
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/compras/${delTarget.id}`);
      toast.success("Compra eliminada");
      setDelTarget(null);
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.cobros) {
        setDelPagos({ factura: delTarget, pagos: detail.cobros });
        setDelTarget(null);
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Error");
    }
  };

  const confirmCascadeDelete = async () => {
    if (!delPagos) return;
    try {
      const { data } = await api.delete(`/compras/${delPagos.factura.id}?cascade=true`);
      if (data.nota_credito_id) {
        toast.success(`Compra eliminada. Se generó una Nota de Crédito automática.`);
      } else {
        toast.success(`Compra y ${data.cobros_eliminados} pago(s) eliminados`);
      }
      setDelPagos(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const runImport = async (file, force = false) => {
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/compras/import-excel${force ? "?force=true" : ""}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${data.compras_creadas} compras · ${data.proveedores_creados} proveedores · ${data.insumos_upsert} insumos`);
      setDupPrompt(null);
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.batch) {
        setDupPrompt({ file, existing: detail.batch });
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await runImport(file, false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const toggleExpand = (id) => setExpanded({ ...expanded, [id]: !expanded[id] });

  const saveNewProveedor = async (e) => {
    e.preventDefault();
    const nombre = newProvForm.nombre.trim();
    if (!nombre) return toast.error("Ingresá el nombre del proveedor");
    setSavingProv(true);
    try {
      const { data } = await api.post("/proveedores", {
        nombre,
        cuit: newProvForm.cuit.trim() || null,
        condicion_iva: newProvForm.condicion_iva,
      });
      toast.success(`Proveedor "${data.nombre}" creado`);
      const { data: cps } = await api.get("/proveedores");
      const ensured = cps.some((c) => c.id === data.id) ? cps : [...cps, data];
      setProveedores(ensured);
      setPendingProvId(data.id);  // se aplicará en useEffect cuando proveedores ya incluya el id
      setNewProvOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear proveedor");
    } finally {
      setSavingProv(false);
    }
  };

  // Aplicar auto-selección del proveedor recién creado UNA VEZ que la lista ya lo incluye
  useEffect(() => {
    if (pendingProvId && proveedores.some((p) => p.id === pendingProvId)) {
      setForm((f) => ({ ...f, contraparte_id: pendingProvId }));
      setPendingProvId(null);
    }
  }, [pendingProvId, proveedores]);

  const reset = () => setFilters(EMPTY_FILTERS);
  const hasFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  const totals = useMemo(() => {
    return compras.reduce(
      (acc, f) => ({
        subtotal: acc.subtotal + (f.subtotal || 0),
        iva: acc.iva + (f.iva || 0),
        percepciones: acc.percepciones + (f.percepciones_total || 0),
        total: acc.total + (f.total || 0),
      }),
      { subtotal: 0, iva: 0, percepciones: 0, total: 0 }
    );
  }, [compras]);

  // Ordenamiento cliente con persistencia y multi-columna (Shift+Click).
  const SORT_STORAGE_KEY = "compras.sort";
  const [sort, setSort] = useState(() => {
    try {
      const raw = localStorage.getItem(SORT_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length && parsed.every((s) => s && s.key && s.dir)) {
        return parsed;
      }
    } catch (_) { /* ignore */ }
    return [{ key: "fecha", dir: "desc" }];
  });
  useEffect(() => {
    try { localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort)); } catch (_) { /* ignore */ }
  }, [sort]);

  const sortValueFor = (f, key) => {
    switch (key) {
      case "fecha":
      case "fecha_vencimiento":
        return (f[key] || "").slice(0, 10);
      case "tipo_comprobante":
      case "numero":
      case "contraparte_nombre":
      case "estado":
        return String(f[key] || "").toLowerCase();
      case "subtotal":
      case "iva":
      case "iva_pct":
      case "percepciones_total":
      case "total":
      case "saldo":
        return Number(f[key] || 0);
      default:
        return f[key];
    }
  };

  const comprasOrdenadas = useMemo(() => {
    // 1) Excluir comprobantes de ajuste del listado de Compras (siguen apareciendo en Cta Cte Proveedores).
    let base = compras.filter((f) => !/^AJUSTE/i.test(f.tipo_comprobante || ""));
    if (filters.solo_con_diferencias) {
      base = base.filter((f) => !!f.tiene_diferencias_stock);
    }
    if (!sort.length) return base;
    return [...base].sort((a, b) => {
      for (const { key, dir } of sort) {
        const va = sortValueFor(a, key);
        const vb = sortValueFor(b, key);
        if (va == null && vb == null) continue;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (va < vb) return dir === "asc" ? -1 : 1;
        if (va > vb) return dir === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [compras, sort]);

  const toggleSort = (key, e) => {
    const shift = !!(e && (e.shiftKey || e.metaKey));
    setSort((prev) => {
      const idx = prev.findIndex((s) => s.key === key);
      if (shift) {
        if (idx === -1) return [...prev, { key, dir: "asc" }];
        const cur = prev[idx];
        const next = [...prev];
        if (cur.dir === "asc") next[idx] = { key, dir: "desc" };
        else next.splice(idx, 1);
        return next.length ? next : [{ key: "fecha", dir: "desc" }];
      }
      if (prev.length === 1 && prev[0].key === key) {
        return [{ key, dir: prev[0].dir === "asc" ? "desc" : "asc" }];
      }
      return [{ key, dir: "asc" }];
    });
  };

  const SortIcon = ({ colKey }) => {
    const idx = sort.findIndex((s) => s.key === colKey);
    const active = idx !== -1;
    const dir = active ? sort[idx].dir : null;
    return (
      <span className="inline-flex flex-col ml-1 align-middle relative" style={{ lineHeight: 0.6 }}>
        <span className={`text-[9px] ${active && dir === "asc" ? "text-[#6B1F24]" : "text-neutral-300"}`}>▲</span>
        <span className={`text-[9px] ${active && dir === "desc" ? "text-[#6B1F24]" : "text-neutral-300"}`}>▼</span>
        {active && sort.length > 1 && (
          <span className="absolute -right-3 top-0 text-[8px] font-mono text-[#6B1F24] font-bold">{idx + 1}</span>
        )}
      </span>
    );
  };
  const SortableTh = ({ colKey, children, className = "", align = "left" }) => (
    <th
      className={`${className} cursor-pointer select-none hover:bg-neutral-100 transition`}
      onClick={(e) => toggleSort(colKey, e)}
      data-testid={`sort-th-compras-${colKey}`}
      title="Click para ordenar · Shift+Click para agregar como criterio secundario"
    >
      <span className={`inline-flex items-center ${align === "right" ? "justify-end w-full" : ""}`}>
        {children}
        <SortIcon colKey={colKey} />
      </span>
    </th>
  );

  return (
    <>
      <Topbar
        title="Compras"
        subtitle={
          `${compras.length} comprobantes · Subtotal ${fmtMoney(totals.subtotal)} · IVA ${fmtMoney(totals.iva)} · Percep. ${fmtMoney(totals.percepciones)} · Total ${fmtMoney(totals.total)}`
        }
        right={
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="import-compras-input"
              onChange={handleImport}
            />
            <Button
              data-testid="new-compra-btn"
              onClick={openNew}
              className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9"
            >
              <Plus size={16} className="mr-1" /> Nueva compra
            </Button>
          </div>
        }
      />

      <div className="p-4 space-y-4">
        <div className="space-y-4">
            {/* Filtros */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm">
          <div className="px-4 py-2 border-b bg-neutral-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FunnelSimple size={14} weight="bold" className="text-[#6B1F24]" />
              <span className="text-[11px] uppercase font-mono tracking-widest text-[#666]">Filtros</span>
            </div>
            {hasFilters && (
              <button
                data-testid="clear-filters-btn"
                onClick={reset}
                className="text-[11px] text-[#666] hover:text-[#6B1F24] flex items-center gap-1 px-2 py-0.5 rounded-sm hover:bg-neutral-100"
              >
                <X size={12} /> Limpiar
              </button>
            )}
          </div>
          <div className="px-4 py-3 grid grid-cols-12 gap-2 text-[11px]">
            <div className="col-span-2">
              <label className="uppercase font-mono text-[#666] tracking-widest">Tipo comprobante</label>
              <Select value={filters.tipo_comprobante} onValueChange={(v) => setFilters({ ...filters, tipo_comprobante: v })}>
                <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {tiposComprobante.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3">
              <label className="uppercase font-mono text-[#666] tracking-widest">Proveedor</label>
              <Select value={filters.contraparte_id} onValueChange={(v) => setFilters({ ...filters, contraparte_id: v })}>
                <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {proveedores.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              <label className="uppercase font-mono text-[#666] tracking-widest">Estado</label>
              <Select value={filters.estado} onValueChange={(v) => setFilters({ ...filters, estado: v })}>
                <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="parcial">Parcial</SelectItem>
                  <SelectItem value="pagada">Pagada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="uppercase font-mono text-[#666] tracking-widest">Desde</label>
              <DateInput value={filters.fecha_desde} onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
                className="rounded-sm h-8 mt-1 text-[11px] font-mono" />
            </div>
            <div className="col-span-2">
              <label className="uppercase font-mono text-[#666] tracking-widest">Hasta</label>
              <DateInput value={filters.fecha_hasta} onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
                className="rounded-sm h-8 mt-1 text-[11px] font-mono" />
            </div>
            <div className="col-span-2">
              <label className="uppercase font-mono text-[#666] tracking-widest">Búsqueda libre</label>
              <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className="rounded-sm h-8 mt-1 text-[12px]" placeholder="Nº, proveedor…" />
            </div>
            <div className="col-span-12 flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => setFilters({ ...filters, solo_con_diferencias: !filters.solo_con_diferencias })}
                data-testid="filter-solo-diferencias"
                className={`text-[11px] font-mono uppercase tracking-wide px-2.5 py-1 rounded-sm border transition-colors ${
                  filters.solo_con_diferencias
                    ? "bg-amber-100 text-amber-900 border-amber-400"
                    : "bg-white text-neutral-600 border-neutral-300 hover:bg-amber-50 hover:border-amber-300"
                }`}
              >
                {filters.solo_con_diferencias ? "✓ " : ""}⚠ Solo con diferencias
                {filters.solo_con_diferencias && (
                  <span className="ml-1 text-neutral-500">
                    ({comprasOrdenadas.length})
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Tabla compras */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] grid-table">
              <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                <tr>
                  <th className="w-8"></th>
                  <SortableTh colKey="fecha" className="px-3 py-2 text-left w-24">Fecha</SortableTh>
                  <SortableTh colKey="fecha_vencimiento" className="px-3 py-2 text-left w-24">Vto.</SortableTh>
                  <SortableTh colKey="tipo_comprobante" className="px-3 py-2 text-left w-32">Tipo</SortableTh>
                  <SortableTh colKey="numero" className="px-3 py-2 text-left w-36">Nº Comprobante</SortableTh>
                  <SortableTh colKey="contraparte_nombre" className="px-3 py-2 text-left">Proveedor</SortableTh>
                  <SortableTh colKey="subtotal" className="px-3 py-2 text-right w-28" align="right">Sin IVA</SortableTh>
                  <SortableTh colKey="iva_pct" className="px-3 py-2 text-right w-14" align="right">IVA%</SortableTh>
                  <SortableTh colKey="iva" className="px-3 py-2 text-right w-24" align="right">IVA</SortableTh>
                  <SortableTh colKey="percepciones_total" className="px-3 py-2 text-right w-24" align="right">Percep.</SortableTh>
                  <SortableTh colKey="total" className="px-3 py-2 text-right w-28" align="right">Total</SortableTh>
                  <SortableTh colKey="saldo" className="px-3 py-2 text-right w-28" align="right">Saldo</SortableTh>
                  <SortableTh colKey="estado" className="px-3 py-2 text-left w-20">Estado</SortableTh>
                  <th className="px-3 py-2 text-center w-20">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {comprasOrdenadas.map((f) => (
                  <Fragment key={f.id}>
                    <tr data-testid={`compra-${f.id}`} className="hover:bg-neutral-50 group">
                      <td className="px-2 text-center">
                        <button
                          data-testid={`expand-${f.id}`}
                          onClick={() => toggleExpand(f.id)}
                          className="p-1 text-[#666] hover:text-[#6B1F24]"
                        >
                          {expanded[f.id] ? <CaretDown size={12} /> : <CaretRight size={12} />}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-mono text-[12px]">{fmtDate(f.fecha)}</td>
                      <td className="px-3 py-2 font-mono text-[12px]">
                        {f.fecha_vencimiento ? (
                          <span className={
                            f.saldo > 0 && f.fecha_vencimiento < todayISO()
                              ? "text-red-600 font-semibold"
                              : ""
                          }>{fmtDate(f.fecha_vencimiento)}</span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200"
                            title="Proveedor con condición 'sin plazo determinado'"
                          >
                            Sin vencimiento
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[12px] text-[#666]">{f.tipo_comprobante || "-"}</td>
                      <td className="px-3 py-2 font-mono text-[12px]">{f.numero}</td>
                      <td className="px-3 py-2 truncate">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate">{f.contraparte_nombre}</span>
                          {f.tiene_diferencias_stock && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300 shrink-0"
                              title={
                                `Recepción con diferencias: ${(f.diferencias_stock_kg > 0 ? "+" : "")}${(f.diferencias_stock_kg || 0).toLocaleString("es-AR", { maximumFractionDigits: 3 })} kg ` +
                                `(${(f.diferencias_stock_items || []).length} ítem${(f.diferencias_stock_items || []).length === 1 ? "" : "s"} con diferencia)`
                              }
                              data-testid={`compra-diffs-${f.id}`}
                            >
                              ⚠ Con diferencias
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(f.subtotal, f.moneda)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] text-[#666]">{fmtNum(f.iva_pct || 0, 1)}%</td>
                      <td className="px-3 py-2 text-right font-mono text-[#666]">{fmtMoney(f.iva, f.moneda)}</td>
                      <td className="px-3 py-2 text-right font-mono text-[#B84A52]">
                        {(f.percepciones_total || 0) > 0 ? fmtMoney(f.percepciones_total, f.moneda) : "-"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmtMoney(f.total, f.moneda)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(f.saldo, f.moneda)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-sm text-[11px] capitalize font-mono ${estadoBadge[f.estado]}`}>
                          {f.estado}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex justify-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                          <button
                            data-testid={`edit-compra-${f.id}`}
                            onClick={() => openEdit(f)}
                            className="p-1 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]"
                          >
                            <PencilSimple size={13} />
                          </button>
                          <button
                            data-testid={`del-compra-${f.id}`}
                            onClick={() => setDelTarget(f)}
                            className="p-1 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600"
                          >
                            <Trash size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded[f.id] && (
                      <tr>
                        <td colSpan={14} className="bg-neutral-50 border-t border-b border-[#E5E5E5]">
                          <div className="p-4 space-y-4">
                            {/* Diferencias vs Recepción (si aplica) */}
                            {f.tiene_diferencias_stock && (f.diferencias_stock_items?.length || 0) > 0 && (
                              <div
                                className="rounded-md border-2 border-amber-300 bg-amber-50 p-3"
                                data-testid={`compra-diffs-panel-${f.id}`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-amber-900 text-[13px]">⚠</span>
                                  <div className="text-[11px] uppercase font-mono text-amber-900 tracking-widest font-semibold">
                                    Diferencias contra la recepción · {f.diferencias_stock_items.length} corte{f.diferencias_stock_items.length === 1 ? "" : "s"}
                                  </div>
                                  <div className="ml-auto text-[11px] font-mono text-amber-900">
                                    Total: <span className="font-bold">
                                      {(f.diferencias_stock_kg > 0 ? "+" : "")}{fmtNum(f.diferencias_stock_kg || 0, 3)} kg
                                    </span>
                                  </div>
                                </div>
                                <table className="w-full text-[12px]">
                                  <thead className="text-[10px] uppercase font-mono text-amber-800">
                                    <tr>
                                      <th className="px-2 py-1 text-left">Corte</th>
                                      <th className="px-2 py-1 text-right w-28">Kg factura</th>
                                      <th className="px-2 py-1 text-right w-28">Kg recibidos</th>
                                      <th className="px-2 py-1 text-right w-28">Diferencia</th>
                                      <th className="px-2 py-1 text-right w-20">% desvío</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {f.diferencias_stock_items.map((d, i) => {
                                      const pf = Number(d.peso_factura || 0);
                                      const pr = Number(d.peso_real || 0);
                                      const diff = Number(d.diferencia_kg || 0);
                                      const pct = pf > 0 ? (diff / pf) * 100 : 0;
                                      const clsDiff = diff > 0 ? "text-emerald-700" : diff < 0 ? "text-red-700" : "text-neutral-600";
                                      return (
                                        <tr key={i} className="border-t border-amber-200">
                                          <td className="px-2 py-1 font-medium">{d.descripcion || "—"}</td>
                                          <td className="px-2 py-1 text-right font-mono">{fmtNum(pf, 3)}</td>
                                          <td className="px-2 py-1 text-right font-mono">{fmtNum(pr, 3)}</td>
                                          <td className={`px-2 py-1 text-right font-mono font-bold ${clsDiff}`}>
                                            {diff > 0 ? "+" : ""}{fmtNum(diff, 3)}
                                          </td>
                                          <td className={`px-2 py-1 text-right font-mono ${clsDiff}`}>
                                            {diff > 0 ? "+" : ""}{fmtNum(pct, 2)}%
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                <div className="mt-2 text-[10px] text-amber-800 italic">
                                  Sólo los kilos de la recepción impactaron en el stock. La diferencia es informativa para analizar el proveedor.
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-6">
                              {/* Items */}
                              <div>
                                <div className="text-[11px] uppercase font-mono text-[#666] tracking-widest mb-2">
                                  Detalle de insumos ({f.items?.length || 0})
                                </div>
                                {f.items && f.items.length > 0 ? (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-[12px]">
                                      <thead className="text-[10px] uppercase font-mono text-[#666]">
                                        <tr>
                                          <th className="px-2 py-1 text-left w-14">Cód.</th>
                                          <th className="px-2 py-1 text-left">Insumo</th>
                                          <th className="px-2 py-1 text-right w-14">Cant.</th>
                                          <th className="px-2 py-1 text-right w-24">P. Unit c/IVA</th>
                                          <th className="px-2 py-1 text-right w-12">IVA%</th>
                                          <th className="px-2 py-1 text-right w-24">Sin IVA</th>
                                          <th className="px-2 py-1 text-right w-24">IVA</th>
                                          <th className="px-2 py-1 text-right w-24">Total c/IVA</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {f.items.map((it, i) => {
                                          const rate = it.iva_pct != null ? it.iva_pct : (f.iva_pct || 0);
                                          const neto = it.subtotal || 0;
                                          const ivaLinea = neto * (rate || 0) / 100;
                                          const totalLinea = neto + ivaLinea;
                                          const punitConIva = (parseFloat(it.precio_unitario) || 0) * (1 + (rate || 0) / 100);
                                          return (
                                            <tr key={i} className="border-t border-[#E5E5E5]">
                                              <td className="px-2 py-1 font-mono text-[#6B1F24]">{it.codigo || "-"}</td>
                                              <td className="px-2 py-1">{it.descripcion}</td>
                                              <td className="px-2 py-1 text-right font-mono">{fmtNum(it.cantidad, 2)}</td>
                                              <td className="px-2 py-1 text-right font-mono">{fmtMoney(punitConIva, f.moneda)}</td>
                                              <td className="px-2 py-1 text-right font-mono text-[#666]">{fmtNum(rate, 1)}%</td>
                                              <td className="px-2 py-1 text-right font-mono">{fmtMoney(neto, f.moneda)}</td>
                                              <td className="px-2 py-1 text-right font-mono text-[#666]">{fmtMoney(ivaLinea, f.moneda)}</td>
                                              <td className="px-2 py-1 text-right font-mono font-semibold">{fmtMoney(totalLinea, f.moneda)}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="text-[#999] text-[12px]">Sin insumos cargados.</div>
                                )}
                              </div>

                              {/* Percepciones */}
                              <div>
                                <div className="text-[11px] uppercase font-mono text-[#666] tracking-widest mb-2">
                                  Percepciones aplicadas ({f.percepciones?.length || 0})
                                </div>
                                {f.percepciones && f.percepciones.length > 0 ? (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-[12px]">
                                      <thead className="text-[10px] uppercase font-mono text-[#666]">
                                        <tr>
                                          <th className="px-2 py-1 text-left">Tipo</th>
                                          <th className="px-2 py-1 text-right w-28">Monto</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {f.percepciones.map((p, i) => (
                                          <tr key={i} className="border-t border-[#E5E5E5]">
                                            <td className="px-2 py-1">{tributoLabel(p.tipo)}</td>
                                            <td className="px-2 py-1 text-right font-mono">{fmtMoney(p.monto, f.moneda)}</td>
                                          </tr>
                                        ))}
                                        <tr className="border-t border-[#E5E5E5] font-semibold">
                                          <td className="px-2 py-1 text-right">Total</td>
                                          <td className="px-2 py-1 text-right font-mono text-[#B84A52]">
                                            {fmtMoney(f.percepciones_total, f.moneda)}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="text-[#999] text-[12px]">Sin percepciones aplicadas.</div>
                                )}
                                {f.observaciones && (
                                  <div className="mt-4 text-[11px] text-[#666]">
                                    <span className="font-mono uppercase text-[10px] tracking-widest">Obs.:</span> {f.observaciones}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Totales generales */}
                            <div className="border border-[#E5E5E5] rounded-sm bg-white p-3 grid grid-cols-2 md:grid-cols-5 gap-4 text-[12px]">
                              <div>
                                <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">Kilos totales</div>
                                <div className="font-mono font-semibold mt-1">
                                  {fmtNum(f.total_kilos || f.items?.reduce((s, it) => s + (parseFloat(it.kilos || it.cantidad) || 0), 0) || 0, 3)} kg
                                </div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">Sin IVA</div>
                                <div className="font-mono font-semibold mt-1">{fmtMoney(f.subtotal || 0, f.moneda)}</div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">IVA {fmtNum(f.iva_pct || 0, 1)}%</div>
                                <div className="font-mono font-semibold mt-1">{fmtMoney(f.iva || 0, f.moneda)}</div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase font-mono text-[#666] tracking-widest">Percepciones</div>
                                <div className="font-mono font-semibold mt-1">{fmtMoney(f.percepciones_total || 0, f.moneda)}</div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase font-mono text-[#6B1F24] tracking-widest">Total</div>
                                <div className="font-mono font-semibold text-[14px] mt-1 text-[#6B1F24]">
                                  {fmtMoney(f.total || 0, f.moneda)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {compras.length === 0 && (
                  <tr><td colSpan={14} className="text-center py-8 text-[#999]">Sin comprobantes de compra.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
          </div>
      </div>

      {/* Sheet crear/editar */}
      <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(initForm()); setPagosAsociados([]); if (prefillIngresoId) { setPrefillIngresoId(null); searchParams.delete("ingreso_id"); setSearchParams(searchParams, { replace: true }); } } }}>
        <SheetContent className="w-full sm:w-[820px] sm:max-w-[820px] overflow-y-auto">
          <SheetHeader>
          </SheetHeader>
          {prefillIngresoId && (
            <div className="mt-3 border border-emerald-200 bg-emerald-50 text-emerald-900 rounded-md p-3 text-sm" data-testid="compra-desde-ingreso-banner">
              📦 <strong>Cargando compra desde Ingreso de Mercadería.</strong> La columna <strong>CANT</strong> ya trae los <em>kilos recibidos</em>. Completá número, tipo de comprobante y el <strong>precio por kg</strong> en P. UNIT.
              {(() => {
                const prefilled = form.items.filter((it) => it._prefill_from_cat).length;
                const sinCosto = form.items.filter((it) => it.producto_id && !it._prefill_from_cat).length;
                if (prefilled === 0 && sinCosto === 0) return null;
                return (
                  <div className="mt-2 text-[12px] font-mono">
                    {prefilled > 0 && (
                      <span className="text-emerald-800">✅ {prefilled} ítem{prefilled === 1 ? "" : "s"} con último costo pre-cargado del catálogo. </span>
                    )}
                    {sinCosto > 0 && (
                      <span className="text-amber-700">⚠️ {sinCosto} ítem{sinCosto === 1 ? "" : "s"} sin costo (nunca comprado o ultimo_costo=0). Cargalo manualmente.</span>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          <form onSubmit={save} className="space-y-4 mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <div>
                <Label className="text-[12px]">Tipo</Label>
                <Select value={form.tipo_comprobante} onValueChange={(v) => setForm({ ...form, tipo_comprobante: v })}>
                  <SelectTrigger data-testid="compra-tipo-comp" className="rounded-sm mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_COMPROBANTE.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Nº Comprobante</Label>
                <Input data-testid="compra-numero" className="rounded-sm mt-1 font-mono" value={form.numero}
                  onChange={(e) => setForm({ ...form, numero: e.target.value })} required placeholder="00001-00000001" />
              </div>
              <div>
                <Label className="text-[12px]">Fecha</Label>
                <DateInput data-testid="compra-fecha" className="rounded-sm mt-1 font-mono" value={form.fecha}
                  onChange={(e) => {
                    const nuevaFecha = e.target.value;
                    const prov = proveedores.find((p) => p.id === form.contraparte_id);
                    const nuevoVto = form.vto_touched
                      ? form.fecha_vencimiento
                      : calcVencimiento(nuevaFecha, prov?.condicion_pago);
                    setForm({ ...form, fecha: nuevaFecha, fecha_vencimiento: nuevoVto });
                  }}
                  required />
              </div>
              <div>
                <Label className="text-[12px]">Vencimiento</Label>
                <DateInput
                  data-testid="compra-fecha-vto"
                  className="rounded-sm mt-1 font-mono"
                  value={form.fecha_vencimiento || form.fecha}
                  onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value, vto_touched: true })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[12px]">Proveedor</Label>
                  <button
                    type="button"
                    data-testid="compra-new-proveedor-btn"
                    onClick={() => { setNewProvForm({ nombre: "", cuit: "", condicion_iva: "Responsable Inscripto" }); setNewProvOpen(true); }}
                    className="text-[10px] text-[#6B1F24] hover:underline font-mono uppercase tracking-widest flex items-center gap-1"
                  >
                    <Plus size={10} weight="bold" /> Crear proveedor
                  </button>
                </div>
                <div className="mt-1">
                  <ProveedorCombobox
                    proveedores={proveedores}
                    value={form.contraparte_id}
                    testId="compra-proveedor"
                    onChange={(v) => {
                      const prov = proveedores.find((c) => c.id === v);
                      const nuevoVto = form.vto_touched
                        ? form.fecha_vencimiento
                        : calcVencimiento(form.fecha, prov?.condicion_pago);
                      // Los checkboxes vienen SIEMPRE tildados por defecto al cargar
                      // una compra (comportamiento pedido por operaciones). Si el operador
                      // ya los editó manualmente (flags_touched), se respeta su decisión.
                      setForm({
                        ...form,
                        contraparte_id: v,
                        fecha_vencimiento: nuevoVto,
                        actualiza_costo: form.flags_touched ? form.actualiza_costo : true,
                      });
                    }}
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px]">Moneda</Label>
                <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARS">ARS</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px]">Cotización</Label>
                <NumberInput className="rounded-sm mt-1 font-mono" step="0.01" value={form.cotizacion}
                  onChange={(e) => setForm({ ...form, cotizacion: e.target.value })} />
              </div>
            </div>

            {/* Items / insumos o productos según tipo de proveedor */}
            {(() => {
              const cpSel = proveedores.find((p) => p.id === form.contraparte_id);
              const tipoProv = cpSel?.tipo_proveedor || "productos";
              const isProductos = tipoProv === "productos";
              const headerLabel = isProductos ? "Productos Vendibles" : "Insumos / Materias Primas";
              const colLabel = isProductos ? "Producto" : "Insumo";
              const comboKind = isProductos ? "productos" : "insumos";
              return (
                <>
                  <div className="border border-[#E5E5E5] rounded-sm">
                    <div className="px-3 py-2 bg-neutral-50 flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                        {headerLabel}
                      </span>
                      <Button type="button" onClick={addItem} size="sm" variant="outline" className="rounded-sm h-7 text-[11px]">
                        <Plus size={12} className="mr-1" /> Agregar
                      </Button>
                    </div>
                    <div className="px-3 py-1.5 grid grid-cols-[repeat(14,minmax(0,1fr))] gap-2 bg-neutral-50/60 border-b border-[#E5E5E5] text-[9px] uppercase font-mono text-[#666] tracking-widest">
                      <div className="col-span-4">{colLabel}</div>
                      <div className="col-span-2 text-right">Cant.</div>
                      <div className="col-span-1 text-right">IVA%</div>
                      <div className="col-span-2 text-right">P. Unit. Neto</div>
                <div className="col-span-2 text-right">P. Unit. c/IVA</div>
                <div className="col-span-2 text-right">Total</div>
                <div className="col-span-1"></div>
              </div>
              <div className="divide-y">
                {form.items.map((it, i) => {
                  const qty = parseFloat(it.cantidad || 0);
                  const neto = parseFloat(it.precio_unitario || 0);
                  const rate = it.iva_pct != null ? parseFloat(it.iva_pct) : parseFloat(form.iva_pct || 0);
                  const bruto = neto * (1 + (rate || 0) / 100);
                  const totalLinea = qty * bruto;

                  const setNeto = (val) => {
                    const items = [...form.items];
                    items[i] = { ...items[i], precio_unitario: val };
                    setForm({ ...form, items });
                  };
                  const setConIva = (val) => {
                    const brutoNum = parseFloat(val || 0);
                    const r = (it.iva_pct != null ? parseFloat(it.iva_pct) : parseFloat(form.iva_pct || 0)) || 0;
                    const netoDerivado = brutoNum / (1 + r / 100);
                    const items = [...form.items];
                    items[i] = { ...items[i], precio_unitario: Number.isFinite(netoDerivado) ? +netoDerivado.toFixed(4) : 0 };
                    setForm({ ...form, items });
                  };
                  const setIvaPct = (v) => {
                    const items = [...form.items];
                    items[i] = { ...items[i], iva_pct: parseFloat(v) };
                    setForm({ ...form, items });
                  };

                  return (
                    <div key={i} className="p-3 grid grid-cols-[repeat(14,minmax(0,1fr))] gap-2 items-start">
                      <div className="col-span-4">
                        {it._concepto_libre ? (
                          <div className="space-y-1">
                            <Input
                              data-testid={`citem-descripcion-${i}`}
                              className="rounded-sm h-8 text-[12px]"
                              placeholder="Concepto (ej: Luz Edenor mayo)"
                              value={it.descripcion}
                              onChange={(e) => setItem(i, "descripcion", e.target.value)}
                            />
                            <Select
                              value={it.categoria_gasto || "otros"}
                              onValueChange={(v) => setItem(i, "categoria_gasto", v)}
                            >
                              <SelectTrigger className="rounded-sm h-7 text-[11px]" data-testid={`citem-categoria-${i}`}>
                                <SelectValue placeholder="Categoría" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="servicios">Servicios (luz, gas, agua, internet)</SelectItem>
                                <SelectItem value="marketing">Marketing / Publicidad</SelectItem>
                                <SelectItem value="impuestos">Impuestos / Tasas</SelectItem>
                                <SelectItem value="otros">Otros</SelectItem>
                              </SelectContent>
                            </Select>
                            <button type="button"
                              className="text-[10px] font-mono text-neutral-500 hover:text-neutral-800 underline"
                              onClick={() => {
                                const items = [...form.items];
                                items[i] = { ...items[i], _concepto_libre: false, descripcion: "", categoria_gasto: null };
                                setForm({ ...form, items });
                              }}
                            >
                              ← elegir del catálogo
                            </button>
                          </div>
                        ) : (
                          <>
                            <ProductoCombobox
                              kind={comboKind}
                              testid={`citem-combo-${i}`}
                              value={{ codigo: it.codigo, nombre: it.descripcion }}
                              onSelect={(p) => {
                                const items = [...form.items];
                                const rateFromInsumo = p.iva_pct != null ? parseFloat(p.iva_pct) : null;
                                // Convención: los productos/insumos guardan `ultimo_costo`
                                // con el IVA incluido (así lo cargó el usuario). Al
                                // precargarlo en el form de compras, va a la columna
                                // "P. Unit. c/IVA" tal cual; y en "P. Unit. Neto"
                                // dividimos por (1 + iva/100).
                                const ivaPct = rateFromInsumo != null ? rateFromInsumo : (parseFloat(items[i].iva_pct) || 0);
                                const ultCostoConIva = parseFloat(p.ultimo_costo || 0);
                                const costoNeto = ultCostoConIva > 0
                                  ? ultCostoConIva / (1 + (ivaPct || 0) / 100)
                                  : 0;
                                items[i] = {
                                  ...items[i],
                                  codigo: p.codigo,
                                  descripcion: p.nombre,
                                  // Guardar el link según el kind del combobox.
                                  insumo_id: isProductos ? null : (p.id || null),
                                  producto_id: isProductos ? (p.id || null) : null,
                                  categoria_gasto: null,
                                  iva_pct: rateFromInsumo != null ? rateFromInsumo : items[i].iva_pct,
                                  precio_unitario: Number.isFinite(costoNeto) ? +costoNeto.toFixed(4) : 0,
                                  unidad: p.unidad || items[i].unidad || "unidad",
                                };
                                setForm({ ...form, items });
                              }}
                            />
                            {!isProductos && (
                              <button type="button"
                                className="mt-1 text-[10px] font-mono text-blue-700 hover:text-blue-900 underline"
                                data-testid={`citem-concepto-libre-${i}`}
                                onClick={() => {
                                  const items = [...form.items];
                                  items[i] = { ...items[i], _concepto_libre: true, insumo_id: null, codigo: null, descripcion: "" };
                                  setForm({ ...form, items });
                                }}
                              >
                                + Concepto libre (sin insumo del catálogo)
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <div className="col-span-2">
                        <MoneyInput className="rounded-sm h-8 font-mono text-[12px] text-right" step="0.001"
                          data-testid={`citem-cant-${i}`}
                          value={it.cantidad} onChange={(e) => setItem(i, "cantidad", e.target.value)} />
                      </div>
                      <div className="col-span-1">
                        <Select value={String(it.iva_pct ?? form.iva_pct)} onValueChange={setIvaPct}>
                          <SelectTrigger data-testid={`citem-iva-${i}`} className="rounded-sm h-8 text-[11px] font-mono">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {IVA_OPCIONES.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <MoneyInput className="rounded-sm h-8 font-mono text-[12px] text-right" step="0.01"
                          data-testid={`citem-neto-${i}`}
                          value={it.precio_unitario}
                          onChange={(e) => setNeto(e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        <MoneyInput className="rounded-sm h-8 font-mono text-[12px] text-right bg-[#F5F1E9]/40" step="0.01"
                          data-testid={`citem-bruto-${i}`}
                          value={Number.isFinite(bruto) ? bruto.toFixed(2) : "0.00"}
                          onChange={(e) => setConIva(e.target.value)} />
                      </div>
                      <div className="col-span-2 text-right font-mono text-[12px] pt-1.5 font-semibold">
                        {totalLinea.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <button type="button" onClick={() => removeItem(i)}
                        className="col-span-1 text-red-600 hover:text-red-800 pt-1.5 flex justify-end">
                        <Trash size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Flag: actualizar costo al confirmar la compra. La compra NO mueve stock. */}
            <div className="border border-blue-200 bg-blue-50/40 rounded-sm p-3 flex flex-wrap items-center gap-4" data-testid="compra-flags">
              <div className="text-[11px] uppercase font-mono text-blue-800 tracking-wider">
                Al confirmar la compra:
              </div>
              <label className="flex items-center gap-2 text-[12px] cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-[#6B1F24]"
                  checked={!!form.actualiza_costo}
                  onChange={(e) => setForm({ ...form, actualiza_costo: e.target.checked, flags_touched: true })}
                  data-testid="compra-actualiza-costo"
                />
                Actualizar costo de última compra
              </label>
              <div className="text-[10px] text-neutral-500 italic ml-2">
                Recordá: el stock lo mueve la Recepción / Ingreso de Mercadería, no la factura.
              </div>
            </div>
              </>
              );
            })()}

            {/* Percepciones */}
            <div className="border border-[#E5E5E5] rounded-sm">
              <div className="px-3 py-2 bg-neutral-50 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Percepciones
                </span>
                <Button type="button" onClick={addPercepcion} size="sm" variant="outline"
                  className="rounded-sm h-7 text-[11px]" data-testid="add-percepcion-btn">
                  <Plus size={12} className="mr-1" /> Agregar
                </Button>
              </div>
              <div className="divide-y">
                {form.percepciones.length === 0 && (
                  <div className="p-3 text-[12px] text-[#999]">
                    Sin percepciones aplicadas. Agregá una si el proveedor las incluye.
                  </div>
                )}
                {form.percepciones.map((p, i) => (
                  <div key={i} className="p-3 grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Label className="text-[10px]">Tipo</Label>
                      <Select value={p.tipo} onValueChange={(v) => setPercepcion(i, "tipo", v)}>
                        <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIPOS_TRIBUTO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Monto</Label>
                      <NumberInput className="rounded-sm h-8 font-mono text-[12px] text-right" step="0.01"
                        value={p.monto} onChange={(e) => setPercepcion(i, "monto", e.target.value)}
                        data-testid={`percepcion-monto-${i}`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Observación</Label>
                      <Input className="rounded-sm h-8 text-[12px]" value={p.observacion || ""}
                        onChange={(e) => setPercepcion(i, "observacion", e.target.value)} />
                    </div>
                    <button type="button" onClick={() => removePercepcion(i)}
                      className="col-span-1 text-red-600 hover:text-red-800 pt-4">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[12px]">Alícuota IVA por defecto</Label>
                <Select value={String(form.iva_pct)} onValueChange={(v) => setForm({ ...form, iva_pct: parseFloat(v) })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="compra-iva-pct"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {IVA_OPCIONES.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex justify-end gap-6 pt-6 font-mono text-[13px]">
                <div>
                  <div className="text-[10px] uppercase text-[#666]">Sin IVA</div>
                  <div>{fmtMoney(subtotal, form.moneda)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#666]">IVA</div>
                  <div>{fmtMoney(iva, form.moneda)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#666]">Percep.</div>
                  <div className="text-[#B84A52]">{fmtMoney(percepcionesTotal, form.moneda)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#666]">Total</div>
                  <div className="font-semibold text-[15px]">{fmtMoney(total, form.moneda)}</div>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[12px]">Observaciones</Label>
              <Input className="rounded-sm mt-1" value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
            </div>

            {(() => {
              // En edición, si hay pagos "complejos" (compartidos o múltiples), no permitir editar
              const exclusivos = pagosAsociados.filter(
                (p) => Array.isArray(p.facturas_aplicadas) && p.facturas_aplicadas.length === 1 && p.facturas_aplicadas[0] === editingId
              );
              const complejo = editingId && (pagosAsociados.length > 1 || pagosAsociados.some((p) => (p.facturas_aplicadas || []).length > 1));
              if (complejo) {
                return (
                  <div className="border border-amber-200 rounded-sm bg-amber-50 p-3 text-[12px] text-amber-800">
                    <div className="font-mono uppercase text-[11px] tracking-widest mb-1">Medio de pago</div>
                    Este comprobante tiene {pagosAsociados.length} pago(s) aplicado(s), algunos compartidos con otras facturas.
                    Editálos manualmente desde Cta. Cte. Proveedores.
                  </div>
                );
              }
              return (
                <div className="border border-[#E5E5E5] rounded-sm bg-[#F5F1E9]/40">
                  <div className="px-3 py-2 bg-[#F5F1E9] border-b border-[#E5E5E5] flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-widest font-mono text-[#6B1F24]">
                      Medio de pago
                    </span>
                    {editingId && form.medio_pago !== form.medio_pago_original && (
                      <span className="text-[10px] uppercase font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-sm">
                        cambio pendiente de guardar
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <Select value={form.medio_pago} onValueChange={(v) => setForm({ ...form, medio_pago: v })}>
                      <SelectTrigger data-testid="compra-medio-pago" className="rounded-sm bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cta_cte">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-sm">
                              CTA CTE
                            </span>
                            <span>Cuenta corriente (pendiente de pago)</span>
                          </div>
                        </SelectItem>
                        {cajas.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono uppercase text-[#666] bg-neutral-100 px-1.5 py-0.5 rounded-sm">
                                {c.tipo}
                              </span>
                              <span>{c.nombre}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })()}

            <Button data-testid="save-compra-btn" type="submit"
              className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]">
              {editingId ? "Guardar cambios" : "Registrar compra"}
            </Button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Duplicate upload */}
      <AlertDialog open={!!dupPrompt} onOpenChange={(v) => { if (!v) { setDupPrompt(null); if (fileRef.current) fileRef.current.value = ""; } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-amber-600" />
              Archivo ya importado
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-[13px]">
                <p>Este Excel ya fue subido anteriormente:</p>
                {dupPrompt && (
                  <div className="border border-[#E5E5E5] rounded-sm p-3 bg-neutral-50 text-[12px] font-mono space-y-1">
                    <div>Archivo: {dupPrompt.existing.filename}</div>
                    <div>Cargado: {new Date(dupPrompt.existing.created_at).toLocaleString("es-AR")}</div>
                    <div>Compras creadas: {dupPrompt.existing.count}</div>
                  </div>
                )}
                <p className="text-amber-700">¿Subirlo igual? Se generarán compras duplicadas.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runImport(dupPrompt.file, true)}
              className="rounded-sm bg-amber-600 hover:bg-amber-700"
            >
              Subir de todas formas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete */}
      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar compra</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar <span className="font-mono">{delTarget?.tipo_comprobante} {delTarget?.numero}</span> de{" "}
              {delTarget?.contraparte_nombre}?<br />
              El saldo del proveedor se revertirá en{" "}
              <span className="font-mono font-semibold">{fmtMoney(delTarget?.total || 0)}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-compra" onClick={confirmDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete cascade — compra tiene pagos asociados */}
      <AlertDialog open={!!delPagos} onOpenChange={(v) => !v && setDelPagos(null)}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-amber-600" />
              La compra tiene pagos asociados
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-[13px]">
                <p>
                  El comprobante <span className="font-mono">{delPagos?.factura.tipo_comprobante} {delPagos?.factura.numero}</span>{" "}
                  tiene {delPagos?.pagos.length} pago(s) vinculado(s).
                </p>
                <div className="border border-amber-200 rounded-sm bg-amber-50 p-3 space-y-2">
                  {delPagos?.pagos.map((c) => (
                    <div key={c.id} className="text-[12px] font-mono">
                      <div>
                        {fmtDate(c.fecha)} · <span className="font-semibold">{fmtMoney(c.monto, c.moneda)}</span>{" "}
                        <span className="text-[#666]">→ {c.caja_nombre}</span>
                      </div>
                      {c.facturas_aplicadas?.length > 1 && (
                        <div className="text-[11px] text-amber-800 mt-0.5">
                          ⚠ Este pago está aplicado a {c.facturas_aplicadas.length} compras.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-red-700 font-semibold">
                  Al confirmar, se eliminarán los pagos listados y se revertirán los saldos de caja y proveedores.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-cascade-delete-compra"
              onClick={confirmCascadeDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Eliminar compra y pagos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revertir batch de compras */}
      <AlertDialog open={!!delBatch} onOpenChange={(v) => !v && setDelBatch(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-amber-600" />
              Revertir importación de compras
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-[13px]">
                <p>
                  ¿Eliminar el lote <span className="font-mono">{delBatch?.filename}</span>?
                </p>
                <p>
                  Se borrarán <span className="font-mono font-semibold">{delBatch?.count}</span> compras y los saldos de proveedores se revertirán en{" "}
                  <span className="font-mono font-semibold">{fmtMoney(delBatch?.total_delta || 0)}</span>.
                </p>
                <p className="text-red-700 pt-1">
                  Los pagos aplicados a estas compras quedarán huérfanos. Revisá primero.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-revert-cbatch"
              onClick={revertBatch}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Revertir lote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {viewBatch && (
        <BatchDetailDialog
          batch={viewBatch}
          onClose={() => setViewBatch(null)}
          onChange={() => { load(); loadBatches(); }}
        />
      )}

      {/* Nuevo proveedor rápido (desde el sheet de compra) */}
      <Dialog open={newProvOpen} onOpenChange={(v) => !savingProv && setNewProvOpen(v)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="new-prov-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuevo proveedor</DialogTitle>
            <DialogDescription className="text-[11px] text-[#666]">
              Se agregará al padrón y quedará seleccionado en esta compra.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveNewProveedor} className="space-y-3">
            <div>
              <Label className="text-[12px]">Nombre / Razón Social *</Label>
              <Input
                autoFocus
                required
                data-testid="new-prov-nombre"
                className="rounded-sm mt-1"
                value={newProvForm.nombre}
                onChange={(e) => setNewProvForm({ ...newProvForm, nombre: e.target.value })}
                placeholder="Ej: Distribuidora XYZ S.A."
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[12px]">CUIT</Label>
                <Input
                  data-testid="new-prov-cuit"
                  className="rounded-sm mt-1 font-mono"
                  value={newProvForm.cuit}
                  onChange={(e) => setNewProvForm({ ...newProvForm, cuit: e.target.value })}
                  placeholder="30-12345678-9"
                />
              </div>
              <div>
                <Label className="text-[12px]">Condición IVA</Label>
                <Select
                  value={newProvForm.condicion_iva}
                  onValueChange={(v) => setNewProvForm({ ...newProvForm, condicion_iva: v })}
                >
                  <SelectTrigger data-testid="new-prov-condicion" className="rounded-sm mt-1 text-[12px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Responsable Inscripto">Responsable Inscripto</SelectItem>
                    <SelectItem value="Monotributo">Monotributo</SelectItem>
                    <SelectItem value="Exento">Exento</SelectItem>
                    <SelectItem value="Consumidor Final">Consumidor Final</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-[10px] text-[#666] font-mono">
              Podés completar el resto de datos luego desde Ctas. Ctes. Proveedores.
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded-sm"
                onClick={() => setNewProvOpen(false)} disabled={savingProv}>
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="new-prov-submit"
                className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
                disabled={savingProv}
              >
                {savingProv ? "Guardando…" : "Crear y seleccionar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}


